import { Button, ButtonGroup } from "@msuite/ui/button";
import { useLoading } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useDataTableContext } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { ProjectMeasurement } from "@msuite/utils/types";
import React from "react";
import { useParams } from "react-router";
import { Can } from "@/components/citadel";
import { client } from "@/hono";

export function ControllingMeasurementMenu() {
	/** Hooks */
	const context = useDataTableContext();
	const { t } = useTranslation();
	const closeLoader = useLoading();
	const openLoader = useLoading();
	const params = useParams();

	const loaders = {
		close: closeLoader,
		open: openLoader,
	};

	/** Functions */
	async function handleOnMark(type: "open" | "close") {
		try {
			loaders[type].start();
			const row = context.table.getCoreRowModel().rows.at(0) as ProjectMeasurement | undefined;
			if (!row) return;
			const batchIds: string[] = context.getSelectedRowIds();
			const projectId = params.projectId as string;
			const ticketAssignmentId = batchIds.at(0)!;
			const response = await client.measurements[":projectId"].measurements[":ticketAssignmentId"][
				type
			].$post({
				param: {
					projectId,
					ticketAssignmentId,
				},
				json: {
					batchIds,
				},
			});
			await responseHandler(response);
			await context.refetch?.();
			context.table.resetRowSelection();
		} catch (error) {
			console.error(error);
		} finally {
			loaders[type].stop();
		}
	}

	/** Render */
	// Both buttons post to the close and open endpoints, which the server gates with
	// `update Measurement` — a rule a member does not hold, because measurements are billing
	// data an organisation grants through a role it creates. Hiding them keeps the toolbar from
	// offering a batch the server refuses.
	return (
		<React.Fragment>
			{context.hasRowSelection && (
				<Can
					action="update"
					subject="Measurement"
				>
					<ButtonGroup isAttached={false}>
						<Button
							onClick={() => handleOnMark("close")}
							isLoading={loaders.close.isLoading}
							icon={<Icon name="Check" />}
						>
							{t("measurements.mark_selected_as_closed")}
						</Button>
						<Button
							onClick={() => handleOnMark("open")}
							isLoading={loaders.open.isLoading}
							icon={<Icon name="RefreshCcw" />}
						>
							{t("measurements.mark_selected_as_open")}
						</Button>
					</ButtonGroup>
				</Can>
			)}
		</React.Fragment>
	);
}
